import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withFormik } from 'formik';
import * as Yup from 'yup';
// components
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikSelect, FormikToggle } from 'shared/forms';
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
import { showConfirmDialogue } from 'shared/modals/ConfirmDialogue';
// fetch
import { upsertLienHolder, deleteLienHolder } from 'src/fetch/lienHolders';
// store
import { getStates } from 'src/slices/states';

const LienHolderModal = ({
  values,
  close,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  isSubmitting,
  setSubmitting,
  resetForm,
}) => {
  const states = useSelector(getStates);
  const dispatch = useDispatch();

  const performDeleteLienHolder = () => {
    setSubmitting(true);
    deleteLienHolder(values.id)
      .then(() => {
        close(true);
        resetForm();

        toastr('success', `Lien Holder Deleted Successfully.`);
      })
      .catch((error) => {
        errorToastr({
          error,
          messagePrefix: 'Lien Holder Delete Failed',
        });
        setSubmitting(false);
      });
  };

  const handleDeleteLienHolder = async () => {
    showConfirmDialogue(dispatch, {
      headerText: 'Delete Lien Holder?',
      text: 'Are you sure you want to delete this Lien Holder?',
      handleConfirm: performDeleteLienHolder,
    });
  };

  return (
    <Modal
      isOpen={true}
      title={`${values && values.id ? 'Edit' : 'Add'} Lien Holder`}
      handleClose={close}
      containerStyle='medium'
    >
      <div className='modal-body-content'>
        <section className={`additional-charges-form ${isSubmitting ? 'hide-overflow' : ''}`}>
          <form onSubmit={handleSubmit}>
            <section className='modal-body formik-form'>
              <section className='formik-column'>
                <div className='formik-row'>
                  <FormikToggle
                    wrapperClass='wrapper-one-column'
                    label='GWI Lien Holder?'
                    name='isGwiLienHolder'
                    value={values.isGwiLienHolder}
                    onToggle={(value) => setFieldValue('isGwiLienHolder', value)}
                    touched={touched}
                    onTouch={() => setFieldTouched('isGwiLienHolder', true)}
                    errors={errors}
                    onText='Yes'
                    offText='No'
                  />
                </div>
                <div className='formik-row'>
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-two-column'
                    label='Name'
                    name='name'
                    required
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-two-column'
                    label='Phone'
                    name='phone'
                    customInput='phone'
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </div>
                <div className='formik-row'>
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-one-column'
                    label='Address'
                    name='address1'
                    value={values.address1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </div>
                <div className='formik-row'>
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-one-column'
                    label='Address 2'
                    name='address2'
                    value={values.address2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </div>
                <div className='formik-row'>
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-three-column'
                    label='City'
                    name='city'
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                  <FormikSelect
                    onChange={(selected) => setFieldValue('state', selected?.value)}
                    wrapperClass='wrapper-three-column'
                    label='State'
                    options={
                      states?.map(({ text, description, value }) => ({ value, label: `${text} - ${description}` })) ??
                      []
                    }
                    name='state'
                    isClearable
                    overflowModal
                    value={values.state}
                    onBlur={setFieldTouched}
                    touched={touched}
                    errors={errors}
                  />
                  <FormikInput
                    type='text'
                    wrapperClass='wrapper-three-column'
                    label='Postal Code'
                    name='postalCode'
                    value={values.postalCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </section>
            </section>
            <section className='modal-footer toolbar toolbar--xxs'>
              <div className='toolbar--column toolbar--right'>
                <button
                  type='submit'
                  className='button large secondary text-align--center toolbar--button--margin'
                  disabled={isSubmitting}
                >
                  Save
                </button>
                {values && values.id && (
                  <button
                    type='button'
                    className='button large error text-align--center toolbar--button--margin'
                    disabled={isSubmitting}
                    onClick={handleDeleteLienHolder}
                  >
                    Delete
                  </button>
                )}
                <button
                  type='button'
                  className='button large white text-align--center margin--none--xxs'
                  onClick={() => close()}
                >
                  Cancel
                </button>
              </div>
            </section>
          </form>
        </section>
      </div>
    </Modal>
  );
};

const TireSizeSchema = Yup.object().shape({
  name: Yup.string().nullable().required('Name is Required'),
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues = {} }) => {
    return {
      ...initialValues,
    };
  },
  validationSchema: TireSizeSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close } }) => {
    setSubmitting(true);

    upsertLienHolder({ ...values })
      .then(() => {
        close(true);
        resetForm();
        toastr('success', `Lien Holder ${values.deleted ? 'Deleted' : 'Saved'} Successfully.`);
      })
      .catch((error) => {
        console.log('Error posting Lien Holder', error);
        errorToastr({
          error,
          messagePrefix: 'Lien Holder Update Failed',
        });
        setSubmitting(false);
      });
  },
})(LienHolderModal);
